/* ============================================================
   Dropdown.jsx — replaces the [data-dd-toggle]/[data-dd-panel] delegation in
   js/app.js:200-215, including its "only one open at a time" behaviour.
   ============================================================ */

import { cloneElement, createContext, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'

const GroupContext = createContext(null)

/** Wrap sibling dropdowns so opening one closes the others. */
export function DropdownGroup({ children }) {
  const [openId, setOpenId] = useState(null)
  const value = useMemo(() => ({ openId, setOpenId }), [openId])
  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>
}

export default function Dropdown({ trigger, children, className = '', panelClassName = '' }) {
  const id = useId()
  const group = useContext(GroupContext)
  const [localOpen, setLocalOpen] = useState(false)
  const ref = useRef(null)

  const open = group ? group.openId === id : localOpen
  const setOpen = (next) => {
    if (group) group.setOpenId(next ? id : null)
    else setLocalOpen(next)
  }

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // The trigger is a render prop returning a single button; stamp the
  // disclosure ARIA on it here so no call site has to remember to.
  const triggerNode = trigger({ open, toggle: () => setOpen(!open) })

  return (
    <div className={`dropdown ${open ? 'open' : ''} ${className}`} ref={ref}>
      {cloneElement(triggerNode, { 'aria-expanded': open, 'aria-haspopup': 'true' })}
      {/* children may be a function of { close } for panels that pick a value and shut. */}
      <div className={`dropdown-menu ${panelClassName}`}>
        {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
      </div>
    </div>
  )
}
